import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import WeeklyPost from '../../components/WeeklyPost/WeeklyPost';
import ContactUs from '../../components/ContactUs/ContactUs';
import styles from './BibleDetailPage.module.css';
import config from '../../config/config';

interface BibleDetail {
  reference: string;
  verses: string;
  explanation?: string;
  detailedTeaching: {
    title: string;
    date: string;
    content: string;
    type: 'bible';
    references: string[];
  };
}

const BibleDetailPage: React.FC = () => {
  const { verseReference } = useParams<{ verseReference: string }>();
  const navigate = useNavigate();
  const [bibleDetail, setBibleDetail] = useState<BibleDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBibleDetail = async () => {
      setLoading(true);
      try {
        // First try to fetch from featured verses API
        const featuredResponse = await axios.get(`${config.API_BASE_URL}/featured-verses/reference/${encodeURIComponent(verseReference || '')}`);
        if (featuredResponse.data) {
          const verseData = featuredResponse.data;
          
          // Ensure the type is 'bible' for the WeeklyPost component
          if (verseData.detailedTeaching && verseData.detailedTeaching.type !== 'bible') {
            verseData.detailedTeaching.type = 'bible';
          }
          
          setBibleDetail(verseData);
          setError(null);
        }
      } catch (featuredError) {
        console.error('Error fetching from featured verses:', featuredError);
        
        try {
          // If featured verses fails, try salvation verses as fallback
          const salvationResponse = await axios.get(`${config.API_BASE_URL}/verses/reference/${encodeURIComponent(verseReference || '')}`);
          if (salvationResponse.data) {
            const verseData = salvationResponse.data;
            
            // Ensure the type is 'bible' for the WeeklyPost component
            if (verseData.detailedTeaching && verseData.detailedTeaching.type !== 'bible') {
              verseData.detailedTeaching.type = 'bible';
            }
            
            setBibleDetail(verseData);
            setError(null);
          }
        } catch (salvationError) {
          console.error('Error fetching from salvation verses:', salvationError);
          
          // Fallback to mock data if enabled in config
          if (config.USE_MOCK_DATA) {
            console.log('Using mock data for Bible verse');
            // For 2 Timothy 3:16-17, we'll use hardcoded data
            if (verseReference === '2 Timothy 3:16-17') {
              const timothyVerse: BibleDetail = {
                reference: '2 Timothy 3:16-17',
                verses: '<i>All scripture is given by inspiration of God, and is profitable for doctrine, for reproof, for correction, for instruction in righteousness: That the man of God may be perfect, throughly furnished unto all good works.</i>',
                explanation: 'This verse reminds us of the importance and purpose of Scripture in our lives.',
                detailedTeaching: {
                  title: 'The Power and Purpose of Scripture',
                  date: 'April 19, 2025',
                  content: 'Let\'s walk through these powerful verses, 2 Timothy 3:16-17, and break them down in a way that\'s straightforward and easy for anyone to grasp. This passage tells us why the Bible is so incredibly special and essential for our lives.\n<h1>Setting the Scene:</h1>\nImagine the Apostle Paul, an older, experienced Christian leader, writing a letter to his younger friend and co-worker, Timothy. Timothy is facing challenges and needs encouragement. Paul points him back to the most reliable thing there is: the Holy Scriptures, the Bible he learned even as a child. Right after reminding him of that, Paul gives us these two verses explaining why the Bible is so trustworthy and important.\n<h2>Verse 16: Where the Bible Comes From and Why It\'s Good For Us</h2>\n<i>"All scripture is given by inspiration of God, and is profitable for doctrine, for reproof, for correction, for instruction in righteousness:"</i>\nLet\'s unpack that:\n<i>"All scripture..."</i>\nThis means the whole Bible, every part of it, from the first page of Genesis to the last page of Revelation. It\'s a complete package.\n<i>"...is given by inspiration of God..."</i>\nThis is the most important part! "Inspiration of God" literally means "God-breathed" (the Greek word is theopneustos). Think of it like this: God Himself is the ultimate author of the Bible. He worked through human writers, but the message and the very words originated with Him.\nBecause it comes directly from God, who is perfect and always tells the truth (Titus 1:2 KJV), the Bible is completely true and trustworthy. We can rely on it 100%. It doesn\'t contain errors; it\'s God\'s pure Word.\n<i>"...and is profitable..."</i>\n"Profitable" just means it\'s useful, helpful, and good for us. God didn\'t give us the Bible just to sit on a shelf. He gave it to help us in real, practical ways. How? Paul lists four ways:\n<i>"...for doctrine":</i> This means the Bible teaches us what is true. It tells us the right things to believe about God, about Jesus, about how to be saved, about how to live. It lays down the facts we need. (What\'s Right)\n<i>"...for reproof":</i> This means the Bible shows us where we are wrong. It points out when our thinking, beliefs, or actions don\'t line up with God\'s truth. It helps us spot sin and error in our lives. (What\'s Not Right)\n<i>"...for correction":</i> This means the Bible shows us how to get back on track. When it points out something wrong (reproof), it also tells us how to fix it and make things right with God and others. (How to Get Right)\n<i>"...for instruction in righteousness":</i> This means the Bible trains us how to live right. It guides us day by day, teaching us how to live in a way that pleases God – how to be kind, honest, prayerful, faithful, etc. (How to Stay Right)\n<h2>Verse 17: The Goal – What the Bible Does For Us</h2>\n<i>"That the man of God may be perfect, throughly furnished unto all good works."</i>\nThis verse tells us the result or the goal of the Bible being God-breathed and helpful:\n<i>"That the man of God..."</i>\nPaul was talking specifically to Timothy, a leader, but this applies to anyone who follows God – any believer who wants to live seriously for Him.\n<i>"...may be perfect..."</i>\nThis doesn\'t mean we become sinless overnight. The word "perfect" here means complete, mature, fully prepared, ready, having everything needed. Think of a toolbox that has every single tool needed for a job, or a soldier who has all the right equipment for battle. The Bible makes us ready and capable for what God wants us to do.\n<i>"...throughly furnished..."</i>\nThis doubles down on the idea of being ready. It means completely equipped or fully supplied. God\'s Word doesn\'t just give us some of what we need; it gives us everything we need.\nThis teaches us the Bible is SUFFICIENT. We have all we need in the Bible for faith and life. We don\'t need to add human philosophies, traditions, or other books to figure out how to know God and live for Him. God\'s Word is enough!\n<i>"...unto all good works."</i>\nWhy does God want us fully equipped? So we can do all the good things He planned for us to do. These "good works" aren\'t things we do to get saved, but things we do because we are saved by His grace (Ephesians 2:8-10 KJV). This includes everything from sharing the Gospel and helping others to living honestly and praying faithfully. The Bible equips us for every part of the Christian life.\n<h2>Putting It All Together (The Main Point):</h2>\nSo, what\'s the big takeaway from these two verses?\nThe Bible isn\'t just another book; it comes directly from God.\nBecause it\'s from God, it\'s completely true and you can trust it fully.\nIt\'s incredibly useful – it teaches us, corrects us, and trains us how to live right.\nThe Bible gives us everything we need to be mature Christians, fully prepared to do all the good things God wants us to do.\n<h2>Think About It:</h2>\nDo you really believe the Bible is God\'s special, inspired Word?\nAre you reading it regularly?\nAre you letting it teach you, point out where you need to change, and guide your daily life?\nAre you trusting that the Bible is enough, or are you looking for answers in other places first?\nLet\'s thank God for giving us His amazing Word! Let\'s make it our priority to read it, believe it, and live by it, so it can fully prepare us for everything He has for us.',
                  type: 'bible',
                  references: ['2 Timothy 3:16-17', 'Titus 1:2', 'Ephesians 2:8-10', 'Psalm 119:105', 'Hebrews 4:12']
                }
              };
              setBibleDetail(timothyVerse);
              setError(null);
            } else {
              setError(`Verse "${verseReference}" not found.`);
            }
          } else {
            setError(`Verse "${verseReference}" not found or API error.`);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    if (verseReference) {
      fetchBibleDetail();
    } else {
      setError('No verse reference provided.');
      setLoading(false);
    }
  }, [verseReference]);

  const handleBackClick = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className={styles['bible-detail-page']}>
        <div className={styles['loading']}>Loading verse details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles['bible-detail-page']}>
        <div className={styles['error']}>
          <h2>Error</h2>
          <p>{error}</p>
          <button className={styles['back-button']} onClick={handleBackClick}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (!bibleDetail) {
    return (
      <div className={styles['bible-detail-page']}>
        <div className={styles['error']}>
          <h2>Verse Not Found</h2>
          <p>The requested verse could not be found.</p>
          <button className={styles['back-button']} onClick={handleBackClick}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles['bible-detail-page']}>
      <div className={styles['bible-header']}>
        <button className={styles['back-button']} onClick={handleBackClick}>
          ← Back to Home
        </button>
        <h1>{bibleDetail.reference}</h1>
      </div>

      <div className={styles['bible-verses']}>
        <h2>Scripture</h2>
        <div className={styles['verse-text']} dangerouslySetInnerHTML={{ __html: bibleDetail.verses }}>
        </div>
      </div>

      <div className={styles['bible-teaching']}>
        <WeeklyPost
          title={bibleDetail.detailedTeaching.title}
          date={bibleDetail.detailedTeaching.date}
          content={bibleDetail.detailedTeaching.content}
          type="bible"
          references={bibleDetail.detailedTeaching.references}
        />
      </div>

      <ContactUs />
    </div>
  );
};

export default BibleDetailPage;
